import { useEffect } from "react";

// Adds `is-visible` to every `.reveal` element once it scrolls into view.
// Under prefers-reduced-motion everything is shown immediately (CSS also disables transitions).
export function useReveal(deps = []) {
    useEffect(() => {
        const els = Array.from(document.querySelectorAll(".reveal:not(.is-visible)"));
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce || !("IntersectionObserver" in window)) { els.forEach((e) => e.classList.add("is-visible")); return undefined; }
        const io = new IntersectionObserver((entries) => {
            entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add("is-visible"); io.unobserve(en.target); } });
        }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
        els.forEach((e) => io.observe(e));
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

// Mouse parallax: writes --mx/--my (-1..1) on the target element. Desktop + motion-allowed only.
export function useParallax(ref) {
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        const fine = window.matchMedia?.("(pointer: fine)").matches;
        if (reduce || !fine) return undefined;
        let raf = 0;
        const onMove = (e) => {
            const r = el.getBoundingClientRect();
            const x = ((e.clientX - r.left) / r.width) * 2 - 1;
            const y = ((e.clientY - r.top) / r.height) * 2 - 1;
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => { el.style.setProperty("--mx", x.toFixed(3)); el.style.setProperty("--my", y.toFixed(3)); });
        };
        const onLeave = () => { el.style.setProperty("--mx", 0); el.style.setProperty("--my", 0); };
        el.addEventListener("mousemove", onMove);
        el.addEventListener("mouseleave", onLeave);
        return () => { el.removeEventListener("mousemove", onMove); el.removeEventListener("mouseleave", onLeave); cancelAnimationFrame(raf); };
    }, [ref]);
}

// Scroll-linked parallax: elements with [data-parallax="0.12"] translate by (distance from viewport center * factor).
export function useScrollParallax() {
    useEffect(() => {
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (reduce) return undefined;
        const els = Array.from(document.querySelectorAll("[data-parallax]"));
        let raf = 0;
        const update = () => {
            raf = 0;
            const vh = window.innerHeight;
            els.forEach((el) => {
                const r = el.getBoundingClientRect();
                if (r.bottom < -200 || r.top > vh + 200) return;
                const f = parseFloat(el.dataset.parallax) || 0.1;
                const d = (r.top + r.height / 2 - vh / 2) * f;
                el.style.setProperty("--sy", `${d.toFixed(1)}px`);
            });
        };
        const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); cancelAnimationFrame(raf); };
    }, []);
}
